import { useGetOrganizations, useUpdateOrganizations } from 'apis/agency/organizations/hook';
import { OrganizationsResponse } from 'apis/agency/organizations/types';
import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import UploadIcon from 'assets/icons/UploadIcon';
import Input from 'components/Common/Input';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

export default function Branding() {
  const { data: organizations } = useGetOrganizations();
  const { mutateAsync: updateOrganizations } = useUpdateOrganizations();
  const [logo, setLogo] = useState<File | null>(null);
  const [favIcon, setFavIcon] = useState<File | null>(null);

  const { control, handleSubmit } = useForm<OrganizationsResponse>({
    values: organizations
  });

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>, type?: string) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      type === 'logo' ? setLogo(file) : setFavIcon(file);
    }
  };

  const onSubmit = async (data: OrganizationsResponse) => {
    const formData = new FormData();
    formData.append('organization[name]', data.name);
    formData.append('organization[support_email]', data.support_email);
    data?.configuration?.brand_color &&
      formData.append('organization[configuration][brand_color]', data.configuration.brand_color);
    logo && formData.append('organization[logo]', logo);
    favIcon && formData.append('organization[favicon]', favIcon);

    await updateOrganizations(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2
        }}
      >
        <Box display='flex' flexDirection='column' gap={3}>
          {/* Header */}
          <Box>
            <Typography variant='h6' fontWeight={600}>
              Business Identity
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.5}>
              Customize how your business appears to customers and team members.
            </Typography>
          </Box>

          {/* Business Name */}
          <Box maxWidth='360px'>
            <Typography fontWeight={300}>Business Name</Typography>
            <Input name='name' control={control} fullWidth size='small' />
            <Typography variant='caption' color='text.secondary' noWrap>
              This name will appear throughout the application and in communications.
            </Typography>
          </Box>
          <Box maxWidth='360px'>
            <Typography fontWeight={300}>Support Email</Typography>
            <Input name='support_email' control={control} fullWidth size='small' />
            <Typography variant='caption' color='text.secondary' noWrap>
              This email will appear throughout the application and in communications.
            </Typography>
          </Box>

          {/* Logo Upload */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Business Logo
            </Typography>

            <Stack direction='row' spacing={4} alignItems='center'>
              {logo || organizations?.logo_url ? (
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#F5F5F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={logo ? URL.createObjectURL(logo) : organizations?.logo_url}
                    alt='Logo'
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Box width={90} height={90} border='1px solid #E0E0E0' borderRadius={1} />
              )}

              <label htmlFor='logo-upload'>
                <input
                  accept='image/*'
                  type='file'
                  name='logo_url'
                  hidden
                  id='logo-upload'
                  onChange={(e) => onUpload(e, 'logo')}
                />
                <Button variant='outlined' component='span' startIcon={<UploadIcon />}>
                  Upload Logo
                </Button>
              </label>
            </Stack>

            <Typography variant='caption' color='text.secondary' mt={1} display='block'>
              Recommended file size is 2MB.
            </Typography>
          </Box>

          {/* Favicon Upload */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Fav Icon
            </Typography>

            <Stack direction='row' spacing={4} alignItems='center'>
              {favIcon || organizations?.favicon_url ? (
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#F5F5F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={favIcon ? URL.createObjectURL(favIcon) : organizations?.favicon_url}
                    alt='Favicon'
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Box width={90} height={90} border='1px solid #E0E0E0' borderRadius={1} />
              )}

              <label htmlFor='favicon-upload'>
                <input
                  accept='image/*'
                  type='file'
                  name='favicon_url'
                  hidden
                  id='favicon-upload'
                  onChange={(e) => onUpload(e, 'favIcon')}
                />
                <Button variant='outlined' component='span' startIcon={<UploadIcon />}>
                  Upload Favicon
                </Button>
              </label>
            </Stack>

            <Typography variant='caption' color='text.secondary' mt={1} display='block'>
              Recommended file size is 2MB.
            </Typography>
          </Box>
          {/* Brand Color Picker */}
          <Box maxWidth='360px'>
            <Typography fontWeight={500} mb={1}>
              Platform Primary Color
            </Typography>
            <Box display='flex' flex='row' gap={1}>
              <Input
                name='configuration.brand_color'
                defaultValue={'#4B22A8'}
                control={control}
                type='color'
                fullWidth
                size='small'
                sx={{ width: '3rem' }}
                inputProps={{ style: { padding: 0, height: 50 } }}
              />
              {/* Hex Code Input */}
              <Box>
                <Input
                  name='configuration.brand_color'
                  control={control}
                  defaultValue={'#4B22A8'}
                  placeholder='#9b87f5'
                  size='small'
                  fullWidth
                  sx={{
                    width: '7rem',
                    input: {
                      fontFamily: 'monospace'
                    }
                  }}
                />
                <Typography fontSize={12}>Enter a valid hex code (e.g., #9b87f5)</Typography>
              </Box>
            </Box>
            <Typography variant='caption' color='text.secondary' noWrap>
              This color will be used for accent elements across your brand.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Footer Buttons - Outside of Paper */}
      <Box mt={3} display='flex' justifyContent='flex-end' gap={2}>
        <Button variant='contained' type='submit'>
          Save Branding
        </Button>
      </Box>
    </form>
  );
}
